import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShoppingBag, LogOut, KeyRound, Percent, Check, Clock, ShoppingCart,
  FileText, X, TrendingUp, ChevronRight, Target, Scale,
  Paperclip, Upload, Eye, FileCheck2, Image as ImageIcon, FileType,
  Shield, ArrowLeftRight, Users, Link2, Copy, Trash2, Plus, Settings
} from 'lucide-react';
import ChatWidget from '../components/ChatWidget';
import VitrinePromocional from '../components/VitrinePromocional';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCurrency = (v) => 'R$ ' + (Number(v) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatDate = (s) => s ? new Date(s).toLocaleDateString('pt-BR') : '-';
const mesAtualLabel = () => new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

export default function VendedorDashboard({ tabInicial = 'overview' }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [comprovantes, setComprovantes] = useState([]);
  const [comprasMin, setComprasMin] = useState([]);
  const [abatimentos, setAbatimentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(tabInicial);
  const [showDetalhesModal, setShowDetalhesModal] = useState(null);
  const [showAnexarModal, setShowAnexarModal] = useState(false);
  const [compraAnexar, setCompraAnexar] = useState(null);
  const [visualizandoCompId, setVisualizandoCompId] = useState(null);
  const [vendasFull, setVendasFull] = useState([]);
  const [vendedorBanner, setVendedorBanner] = useState('');
  const [vendedorBannerLink, setVendedorBannerLink] = useState('');

  useEffect(() => { setActiveTab(tabInicial); }, [tabInicial]);

  useEffect(() => {
    // Carrega banner do vendedor configurado pelo admin
    axios.get(`${API}/public/settings`)
      .then(r => {
        setVendedorBanner(r.data?.vendedor_banner_url || '');
        setVendedorBannerLink(r.data?.vendedor_banner_link || '');
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    if (user.must_change_password) { navigate('/vendedor/trocar-senha'); return; }
    if (user.role !== 'vendedor' && user.role !== 'admin') {
      toast.error('Acesso restrito a vendedores');
      navigate('/login');
      return;
    }
    carregar();
  }, [user]);

  const carregar = async () => {
    try {
      const [d, c, cmp, ab, vf] = await Promise.all([
        axios.get(`${API}/vendedor/me/dashboard`),
        axios.get(`${API}/vendedor/me/comprovantes`),
        axios.get(`${API}/vendedor/me/romaneios-compras-min`),
        axios.get(`${API}/vendedor/me/abatimentos`),
        axios.get(`${API}/vendedor/me/romaneios-vendas`).catch(() => ({ data: [] })),
      ]);
      setDashboard(d.data);
      setComprovantes(c.data || []);
      setComprasMin(cmp.data || []);
      setAbatimentos(ab.data || []);
      setVendasFull(vf.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const recarregarComprovantes = async () => {
    try {
      const [c, cmp] = await Promise.all([
        axios.get(`${API}/vendedor/me/comprovantes`),
        axios.get(`${API}/vendedor/me/romaneios-compras-min`),
      ]);
      setComprovantes(c.data || []);
      setComprasMin(cmp.data || []);
    } catch (e) { /* ignore */ }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  if (!user || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-gray-600 dark:text-gray-300">Carregando...</p>
        </div>
      </div>
    );
  }

  const dc = dashboard?.comissao || {};
  const dcp = dashboard?.compras || {};
  const ds = dashboard?.saldo || {};
  const dv = dashboard?.vendas || {};
  const percentual = dashboard?.vendedor?.percentual_comissao || 0;
  const totalReceber = (dc.mes_atual || 0) + (dc.meses_anteriores_pendente || 0);

  return (
    <div className="portal-page-bg min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
              {(user.name || '?').charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 className="text-base font-bold text-gray-900 dark:text-white" data-testid="vendedor-nome-header">
                Olá, {user.name}
              </h1>
              <p className="text-xs text-gray-500">{percentual}% de comissão · {mesAtualLabel()}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              const t = toast.loading('Gerando extrato PDF...');
              try {
                const sellerId = user?.seller_id;
                if (!sellerId) { toast.error('Seu perfil não tem seller_id vinculado', { id: t }); return; }
                const resp = await axios.get(`${API}/sellers/${sellerId}/extrato/pdf`, { responseType: 'blob' });
                const url = URL.createObjectURL(resp.data);
                window.open(url, '_blank');
                setTimeout(() => URL.revokeObjectURL(url), 5000);
                toast.success('Extrato pronto', { id: t });
              } catch (e) {
                toast.error('Erro ao gerar extrato', { id: t });
              }
            }}
            className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-semibold shadow hover:shadow-lg transition"
            data-testid="btn-extrato-pdf-vendedor"
            title="Meu extrato completo em PDF"
          >
            <FileText className="w-4 h-4" /> <span className="hidden sm:inline">Meu Extrato</span>
          </button>
          <button
            onClick={() => navigate('/vendedor/pedido')}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg text-sm font-semibold shadow hover:shadow-lg transition"
            data-testid="btn-gerar-pedido"
          >
            <ShoppingCart className="w-4 h-4" /> Gerar Pedido
          </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-5">
        {/* Banner do Vendedor (configurado pelo admin) */}
        {vendedorBanner && (
          <a
            href={vendedorBannerLink || '#'}
            target={vendedorBannerLink ? '_blank' : undefined}
            rel="noopener noreferrer"
            onClick={(e) => { if (!vendedorBannerLink) e.preventDefault(); }}
            className="block relative rounded-2xl overflow-hidden shadow-lg"
            data-testid="vendedor-banner"
          >
            <img src={vendedorBanner} alt="Banner Vendedor" className="w-full h-32 sm:h-40 object-cover" />
          </a>
        )}

        {/* Vitrines Promocionais (Mais Vendidos, Ofertas, etc.) */}
        <VitrinePromocional portal="vendedor" onProductClick={(p) => navigate(`/vendedor/pedido?produto=${p.id}`)} showPrice />

        {/* 4 CARDS principais */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Comissão a Receber */}
          <button onClick={() => setShowDetalhesModal('comissao_receber')} className="text-left bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border-2 border-orange-100 dark:border-orange-900/30 hover:border-orange-300 hover:shadow-md transition group" data-testid="card-comissao-receber">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center"><Percent className="w-5 h-5 text-orange-600" /></div>
              <ChevronRight className="w-4 h-4 text-gray-400 group-hover:translate-x-1 transition" />
            </div>
            <p className="text-xs font-semibold text-orange-700 dark:text-orange-400 uppercase tracking-wide">Comissão a Receber</p>
            <p className="text-2xl font-extrabold text-orange-600 mt-1" data-testid="value-comissao-receber">{formatCurrency(totalReceber)}</p>
            <p className="text-[11px] text-gray-500 mt-1">Mês atual: <b>{formatCurrency(dc.mes_atual || 0)}</b></p>
            <p className="text-[11px] text-gray-500">Meses passados em aberto: <b className="text-red-600">{formatCurrency(dc.meses_anteriores_pendente || 0)}</b></p>
          </button>

          {/* Comissão Paga */}
          <button onClick={() => setShowDetalhesModal('comissao_paga')} className="text-left bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border-2 border-green-100 dark:border-green-900/30 hover:border-green-300 hover:shadow-md transition group" data-testid="card-comissao-paga">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center"><Check className="w-5 h-5 text-green-600" /></div>
              <ChevronRight className="w-4 h-4 text-gray-400 group-hover:translate-x-1 transition" />
            </div>
            <p className="text-xs font-semibold text-green-700 dark:text-green-400 uppercase tracking-wide">Comissão Paga</p>
            <p className="text-2xl font-extrabold text-green-600 mt-1" data-testid="value-comissao-paga">{formatCurrency(dc.paga || 0)}</p>
            <p className="text-[11px] text-gray-500 mt-1">Total já recebido por você</p>
          </button>

          {/* Falta Pagar */}
          <button onClick={() => setShowDetalhesModal('falta_pagar')} className="text-left bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border-2 border-amber-100 dark:border-amber-900/30 hover:border-amber-300 hover:shadow-md transition group" data-testid="card-falta-pagar">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center"><Clock className="w-5 h-5 text-amber-600" /></div>
              <ChevronRight className="w-4 h-4 text-gray-400 group-hover:translate-x-1 transition" />
            </div>
            <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wide">Falta Pagar (Loja → Você)</p>
            <p className="text-2xl font-extrabold text-amber-600 mt-1" data-testid="value-falta-pagar">{formatCurrency(dc.falta_pagar || 0)}</p>
            <p className="text-[11px] text-gray-500 mt-1">Comissão pendente (todos os meses)</p>
          </button>

          {/* Você Deve à Loja */}
          <button onClick={() => setShowDetalhesModal('compras')} className="text-left bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border-2 border-red-100 dark:border-red-900/30 hover:border-red-300 hover:shadow-md transition group" data-testid="card-voce-deve">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center"><ShoppingCart className="w-5 h-5 text-red-600" /></div>
              <ChevronRight className="w-4 h-4 text-gray-400 group-hover:translate-x-1 transition" />
            </div>
            <p className="text-xs font-semibold text-red-700 dark:text-red-400 uppercase tracking-wide">Você Deve à Loja</p>
            <p className="text-2xl font-extrabold text-red-600 mt-1" data-testid="value-voce-deve">{formatCurrency(dcp.em_aberto || 0)}</p>
            <p className="text-[11px] text-gray-500 mt-1">{dcp.qtd_em_aberto || 0} compra(s) em aberto</p>
          </button>
        </div>

        {/* Saldo Líquido se compensar Comissão · Compras */}
        <button onClick={() => setShowDetalhesModal('saldo')} className="w-full text-left bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 border-2 border-indigo-200 dark:border-indigo-800 rounded-xl p-4 hover:shadow-md transition group" data-testid="card-saldo-liquido">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1">
              <div className="w-11 h-11 bg-indigo-100 dark:bg-indigo-900/40 rounded-lg flex items-center justify-center"><Scale className="w-5 h-5 text-indigo-600" /></div>
              <div className="flex-1">
                <p className="text-xs font-bold text-indigo-700 dark:text-indigo-300 uppercase tracking-wide">Saldo Líquido se compensar Comissão · Compras</p>
                <p className="text-2xl font-extrabold text-indigo-700 dark:text-indigo-300" data-testid="value-saldo-liquido">{formatCurrency(ds.divida_liquida || 0)}</p>
                <p className="text-[11px] text-indigo-700 dark:text-indigo-300/80 mt-0.5">{ds.explicacao || ''}</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-indigo-500 group-hover:translate-x-1 transition" />
          </div>
        </button>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700 flex items-center gap-1 overflow-x-auto">
          {[
            { k: 'overview', label: 'Visão Geral', icon: TrendingUp },
            { k: 'vendas', label: `Minhas Vendas (${vendasFull.length || dv.qtd || 0})`, icon: ShoppingBag },
            { k: 'compras', label: `Minhas Compras (${dcp.qtd_total || 0})`, icon: ShoppingCart },
          ].map(t => (
            <button
              key={t.k}
              onClick={() => setActiveTab(t.k)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${activeTab === t.k ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              data-testid={`tab-${t.k}`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'overview' && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
            <p className="text-sm font-semibold text-blue-900 dark:text-blue-200 flex items-center gap-2"><Target className="w-4 h-4" /> Seu mês ({mesAtualLabel()})</p>
            <p className="text-xs text-blue-800 dark:text-blue-300 mt-1">
              {dv.qtd ? `${dv.qtd} venda${dv.qtd !== 1 ? 's' : ''} registrada${dv.qtd !== 1 ? 's' : ''} neste mês.` : 'Nenhuma venda registrada este mês ainda.'}
            </p>
          </div>
        )}
        {activeTab === 'vendas' && <RomaneiosListSimples items={vendasFull} type="vendas" />}
        {activeTab === 'compras' && <RomaneiosListSimples items={dcp.detalhes || []} type="compras" />}

        {/* Comprovantes */}
        <ComprovantesSection comprovantes={comprovantes} onVisualizar={(id) => setVisualizandoCompId(id)} />

        {/* Abatimentos audit */}
        <AbatimentosSection abatimentos={abatimentos} />

        {/* Compras com botão anexar */}
        <ComprasSection compras={comprasMin} comprovantes={comprovantes} onAnexar={(rom) => { setCompraAnexar(rom); setShowAnexarModal(true); }} onVisualizar={(id) => setVisualizandoCompId(id)} />

        {/* Info conta */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 md:col-span-2">
            <p className="text-xs text-gray-500 mb-2 uppercase tracking-wide font-semibold">Informações da conta</p>
            <div className="text-sm space-y-1 text-gray-700 dark:text-gray-300">
              <p><b>Nome:</b> {user.name}</p>
              <p><b>E-mail:</b> {user.email}</p>
              <p><b>Comissão padrão:</b> {percentual}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showDetalhesModal && (
        <DetalhesModal tipo={showDetalhesModal} dashboard={dashboard} onClose={() => setShowDetalhesModal(null)} />
      )}
      {showAnexarModal && compraAnexar && (
        <AnexarComprovanteModal romaneio={compraAnexar} onClose={() => { setShowAnexarModal(false); setCompraAnexar(null); }} onSuccess={() => { setShowAnexarModal(false); setCompraAnexar(null); recarregarComprovantes(); }} />
      )}
      {visualizandoCompId && (
        <VisualizarComprovanteModal fileId={visualizandoCompId} onClose={() => setVisualizandoCompId(null)} />
      )}
      <ChatWidget />
    </div>
  );
}


function RomaneiosListSimples({ items, type }) {
  if (!items || items.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 text-center py-10">
        <FileText className="w-10 h-10 mx-auto text-gray-300 mb-2" />
        <p className="text-sm text-gray-500">Nenhum item ainda.</p>
      </div>
    );
  }

  const formaPagLabel = (f) => {
    const map = { pago: 'Pago', avista: 'À Vista', a_receber: 'A Receber',
                  boleto: 'Boleto', pix: 'PIX', cartao: 'Cartão', prazo: 'A Prazo' };
    return map[(f || '').toLowerCase()] || f || '-';
  };
  const statusBadge = (r) => {
    const forma = (r.forma_pagamento || '').toLowerCase();
    const s = (r.status_pagamento || '').toLowerCase();
    if (s === 'pago' || forma === 'pago' || forma === 'avista') {
      return <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px] font-bold">Pago</span>;
    }
    if (s === 'parcial') return <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold">Parcial</span>;
    if (s === 'atrasado') return <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold">Atrasado</span>;
    return <span className="px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 text-[10px] font-bold">Pendente</span>;
  };
  const prazoResumo = (r) => {
    const parcs = (r.parcelas_prazo || []).filter(Boolean);
    if (parcs.length > 0) {
      return `${parcs.length}x — 1º ${formatDate(parcs[0])}`;
    }
    if (r.data_vencimento_boleto) return `Venc: ${formatDate(r.data_vencimento_boleto)}`;
    if (r.dias_para_receber) return `${r.dias_para_receber} dias`;
    if ((r.forma_pagamento || '').toLowerCase() === 'avista' || (r.forma_pagamento || '').toLowerCase() === 'pago') return 'À Vista';
    return '-';
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-x-auto" data-testid={`list-${type}`}>
      <table className="w-full text-xs">
        <thead className="bg-gray-50 dark:bg-gray-700/50">
          <tr className="text-gray-600 dark:text-gray-300 uppercase text-[10px] tracking-wide">
            <th className="px-3 py-2 text-left">Pedido</th>
            <th className="px-3 py-2 text-left">Data</th>
            <th className="px-3 py-2 text-left">{type === 'vendas' ? 'Cliente' : 'Vendedor'}</th>
            <th className="px-3 py-2 text-right">Valor</th>
            <th className="px-3 py-2 text-left">Forma</th>
            <th className="px-3 py-2 text-left">Prazo / Venc.</th>
            <th className="px-3 py-2 text-left">Data Pagto</th>
            <th className="px-3 py-2 text-center">Status</th>
            <th className="px-3 py-2 text-right">{type === 'vendas' ? 'Comissão' : 'Em aberto'}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
          {items.map((r, i) => (
            <tr key={r.id || i} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 text-gray-800 dark:text-gray-200">
              <td className="px-3 py-2 font-semibold whitespace-nowrap">#{r.numero_pedido || '-'}</td>
              <td className="px-3 py-2 whitespace-nowrap text-gray-500">{formatDate(r.data || r.created_at)}</td>
              <td className="px-3 py-2 truncate max-w-[180px]">{r.cliente_nome || r.vendedor_nome || '-'}</td>
              <td className="px-3 py-2 text-right whitespace-nowrap font-medium">{formatCurrency(r.valor_total || 0)}</td>
              <td className="px-3 py-2 whitespace-nowrap">{formaPagLabel(r.forma_pagamento)}</td>
              <td className="px-3 py-2 whitespace-nowrap text-gray-500">{prazoResumo(r)}</td>
              <td className="px-3 py-2 whitespace-nowrap text-gray-500">{r.data_pagamento ? formatDate(r.data_pagamento) : '-'}</td>
              <td className="px-3 py-2 text-center">{statusBadge(r)}</td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                <span className="font-bold text-orange-600">{formatCurrency(r.comissao_valor || r.comissao_pendente || r.em_aberto || 0)}</span>
                {type === 'vendas' && r.comissao_paga && (
                  <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-bold">PAGA</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function DetalhesModal({ tipo, dashboard, onClose }) {
  const titulos = {
    comissao_receber: 'Comissão a Receber',
    comissao_paga: 'Comissão Paga',
    falta_pagar: 'Falta Pagar (Loja → Você)',
    compras: 'Suas Compras à Loja',
    saldo: 'Saldo Líquido se compensar',
  };
  let lista = [];
  if (tipo === 'comissao_receber') lista = [...(dashboard?.comissao?.detalhes_mes_atual || []), ...(dashboard?.comissao?.detalhes_meses_passados || [])];
  else if (tipo === 'comissao_paga') lista = dashboard?.comissao?.detalhes_pagamentos || [];
  else if (tipo === 'falta_pagar') lista = [...(dashboard?.comissao?.detalhes_mes_atual || []), ...(dashboard?.comissao?.detalhes_meses_passados || [])];
  else if (tipo === 'compras') lista = dashboard?.compras?.detalhes || [];

  // Escolhe qual campo de valor mostrar por tipo (o valor específico da coluna clicada)
  const getValor = (r) => {
    if (tipo === 'comissao_receber' || tipo === 'falta_pagar') return r.comissao_pendente || 0;
    if (tipo === 'comissao_paga') return r.comissao_paga || 0;
    if (tipo === 'compras') return r.saldo || r.valor_total || 0;
    return r.valor_total || 0;
  };

  const total = lista.reduce((s, r) => s + getValor(r), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose} data-testid={`detalhes-modal-${tipo}`}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[88vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white flex items-center justify-between">
          <div>
            <h3 className="font-bold">{titulos[tipo]}</h3>
            {tipo !== 'saldo' && (
              <p className="text-xs opacity-90 mt-0.5">
                {lista.length} romaneio{lista.length !== 1 ? 's' : ''} · Total {formatCurrency(total)}
              </p>
            )}
          </div>
          <button onClick={onClose} className="hover:bg-white/20 p-1.5 rounded"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {tipo === 'saldo' ? (
            <div className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
              <p className="text-2xl font-bold text-indigo-600">{formatCurrency(dashboard?.saldo?.divida_liquida || 0)}</p>
              <p>{dashboard?.saldo?.explicacao || ''}</p>
            </div>
          ) : lista.length === 0 ? (
            <p className="text-sm text-gray-500 italic text-center py-8">Sem itens.</p>
          ) : (
            <div className="space-y-2">
              {lista.map((r, i) => (
                <div key={r.id || i} className="bg-gray-50 dark:bg-gray-700/40 rounded-lg p-3 text-sm flex justify-between items-center" data-testid={`detalhes-item-${r.id || i}`}>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold truncate">
                      #{r.numero_pedido} <span className="text-xs text-gray-500">— {r.cliente_nome || r.vendedor_nome || '-'}</span>
                    </p>
                    <p className="text-[11px] text-gray-500">{formatDate(r.created_at || r.data || r.data_pagamento)}</p>
                    {(tipo === 'comissao_receber' || tipo === 'falta_pagar') && (
                      <p className="text-[10px] text-gray-500 mt-0.5">
                        Venda: {formatCurrency(r.valor_total || 0)} · Comissão devida: {formatCurrency(r.comissao_devida || 0)}
                        {r.comissao_paga ? ` · Já paga: ${formatCurrency(r.comissao_paga)}` : ''}
                      </p>
                    )}
                    {tipo === 'compras' && (
                      <p className="text-[10px] text-gray-500 mt-0.5">
                        Compra: {formatCurrency(r.valor_total || 0)} · Já pago: {formatCurrency(r.valor_pago || 0)}
                      </p>
                    )}
                  </div>
                  <p className="font-bold text-orange-600 whitespace-nowrap ml-3" data-testid={`detalhes-valor-${r.id || i}`}>
                    {formatCurrency(getValor(r))}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function ComprovantesSection({ comprovantes, onVisualizar }) {
  if (!comprovantes || comprovantes.length === 0) return null;
  const tipoLabel = (t) => t === 'comissao' ? 'Comissão Paga' : 'Pagamento de Compra';
  const tipoBadgeClass = (t) => t === 'comissao' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden" data-testid="section-comprovantes">
      <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
        <FileCheck2 className="w-4 h-4 text-green-600" />
        <h2 className="text-sm font-bold">Meus Comprovantes ({comprovantes.length})</h2>
      </div>
      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {comprovantes.map(c => {
          const isPdf = (c.content_type || '').includes('pdf');
          return (
            <div key={c.id} className="px-5 py-3 flex items-center gap-3" data-testid={`comprovante-${c.id}`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isPdf ? 'bg-red-100 text-red-600 dark:bg-red-900/30' : 'bg-blue-100 text-blue-600 dark:bg-blue-900/30'}`}>
                {isPdf ? <FileType className="w-5 h-5" /> : <ImageIcon className="w-5 h-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${tipoBadgeClass(c.payment_type)}`}>{tipoLabel(c.payment_type)}</span>
                  <span className="text-xs font-semibold">Romaneio #{c.romaneio_numero || '-'}</span>
                </div>
                <p className="text-sm font-medium truncate">{c.original_filename}</p>
                <div className="text-[11px] text-gray-500 mt-0.5 flex flex-wrap gap-x-3">
                  {c.cliente_nome && <span>Cliente: <b>{c.cliente_nome}</b></span>}
                  {c.valor_pagamento !== undefined && c.valor_pagamento !== null && <span>Valor: <b>{formatCurrency(c.valor_pagamento)}</b></span>}
                  {c.data_pagamento && <span>{formatDate(c.data_pagamento)}</span>}
                </div>
              </div>
              <button onClick={() => onVisualizar(c.id)} className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-testid={`btn-ver-comprovante-${c.id}`}>
                <Eye className="w-3.5 h-3.5" /> Ver
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function AbatimentosSection({ abatimentos }) {
  if (!abatimentos || abatimentos.length === 0) return null;
  const total = abatimentos.reduce((s, a) => s + (Number(a.valor) || 0), 0);
  const tipoLabel = (t) => t === 'auto_offset' ? 'Auto-abatimento' : (t === 'manual_cascata' ? 'Manual (cascata)' : (t || '-'));
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden" data-testid="section-abatimentos">
      <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
        <Shield className="w-4 h-4 text-indigo-600" />
        <h2 className="text-sm font-bold">Histórico de Abatimentos ({abatimentos.length})</h2>
        <span className="text-[10px] text-gray-500 ml-auto">Total: <b className="text-indigo-600">{formatCurrency(total)}</b></span>
      </div>
      <div className="px-5 py-2 bg-indigo-50/50 dark:bg-indigo-900/10 border-b border-indigo-100 dark:border-indigo-800/30">
        <p className="text-[11px] text-indigo-800 dark:text-indigo-300 flex items-center gap-1"><Shield className="w-3 h-3" /> Registro imutável (chain-hash).</p>
      </div>
      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {abatimentos.map(a => (
          <div key={a.id} className="px-5 py-3 flex items-center gap-3" data-testid={`abatimento-${a.id}`}>
            <div className="w-10 h-10 bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center"><ArrowLeftRight className="w-5 h-5" /></div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">{tipoLabel(a.tipo)}</span>
                {a.romaneio_comissao_numero && <span className="text-xs font-semibold">Comissão #{a.romaneio_comissao_numero}</span>}
              </div>
              {a.descricao && <p className="text-[11px] text-gray-600 dark:text-gray-400">{a.descricao}</p>}
              <p className="text-[10px] text-gray-400">{formatDate(a.created_at)}</p>
            </div>
            <p className="text-sm font-bold text-indigo-600">{formatCurrency(a.valor)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}


function ComprasSection({ compras, comprovantes, onAnexar, onVisualizar }) {
  if (!compras || compras.length === 0) return null;
  const cpc = comprovantes.filter(c => c.payment_type === 'pagamento').reduce((acc, c) => {
    (acc[c.romaneio_id] = acc[c.romaneio_id] || []).push(c);
    return acc;
  }, {});
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden" data-testid="section-compras">
      <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
        <ShoppingCart className="w-4 h-4 text-purple-600" />
        <h2 className="text-sm font-bold">Minhas Compras à Loja ({compras.length})</h2>
        <span className="text-[10px] text-gray-500 ml-auto">Anexe aqui o comprovante quando pagar</span>
      </div>
      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {compras.map(c => {
          const meus = cpc[c.id] || [];
          return (
            <div key={c.id} className="px-5 py-3" data-testid={`compra-${c.numero_pedido}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <div>
                  <p className="text-sm font-bold">#{c.numero_pedido || '-'}</p>
                  <p className="text-[11px] text-gray-500">{formatDate(c.created_at || c.data)}</p>
                </div>
                <div className="flex-1 min-w-[150px]">
                  <p className="text-xs text-gray-500">Valor</p>
                  <p className="text-sm font-bold text-purple-600">{formatCurrency(c.valor_total)}</p>
                </div>
                <button onClick={() => onAnexar(c)} className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold bg-purple-600 text-white rounded-lg hover:bg-purple-700" data-testid={`btn-anexar-${c.numero_pedido}`}>
                  <Upload className="w-3.5 h-3.5" /> Anexar Comprovante
                </button>
              </div>
              {meus.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {meus.map(m => (
                    <button key={m.id} onClick={() => onVisualizar(m.id)} className="flex items-center gap-1 text-[11px] bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-full hover:bg-blue-100">
                      <Paperclip className="w-3 h-3" /> {m.original_filename}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


function AnexarComprovanteModal({ romaneio, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [paymentIndex, setPaymentIndex] = useState(0);
  const [descricao, setDescricao] = useState('');
  const [uploading, setUploading] = useState(false);
  const pagamentos = romaneio.pagamentos_registrados || [];
  const temPagamentos = pagamentos.length > 0;
  const handleUpload = async () => {
    if (!file) { toast.error('Selecione um arquivo'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Arquivo maior que 10MB'); return; }
    if (!temPagamentos) { toast.error('Esta compra ainda não tem pagamento registrado pelo admin.'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('payment_type', 'pagamento');
      fd.append('payment_index', String(paymentIndex));
      fd.append('descricao', descricao);
      fd.append('file', file);
      await axios.post(`${API}/romaneios/${romaneio.id}/comprovantes`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Comprovante anexado!');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro');
    } finally { setUploading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="modal-anexar-comprovante">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="px-5 py-4 bg-gradient-to-r from-purple-600 to-purple-700 text-white flex items-center justify-between">
          <div>
            <h3 className="font-bold">Anexar Comprovante</h3>
            <p className="text-xs opacity-90">Romaneio #{romaneio.numero_pedido} · {formatCurrency(romaneio.valor_total)}</p>
          </div>
          <button onClick={onClose} className="hover:bg-white/20 p-1.5 rounded"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {!temPagamentos ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">⚠️ Peça pra loja registrar o pagamento antes de anexar comprovante.</div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-semibold mb-1">Qual pagamento?</label>
                <select value={paymentIndex} onChange={(e) => setPaymentIndex(Number(e.target.value))} className="w-full px-3 py-2 text-sm border rounded">
                  {pagamentos.map((p, i) => <option key={i} value={i}>#{i + 1} — {formatCurrency(p.valor)} · {formatDate(p.data)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">Arquivo (PDF/JPG/PNG até 10MB)</label>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,image/*,application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" />
                {file && <p className="text-[11px] text-gray-500 mt-1">{file.name}</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">Descrição (opcional)</label>
                <input type="text" value={descricao} onChange={(e) => setDescricao(e.target.value)} className="w-full px-3 py-2 text-sm border rounded" />
              </div>
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t bg-gray-50 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded hover:bg-gray-200">Cancelar</button>
          <button onClick={handleUpload} disabled={uploading || !file || !temPagamentos} className="px-5 py-2 text-sm font-semibold bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2">
            <Upload className="w-4 h-4" /> {uploading ? 'Enviando...' : 'Anexar'}
          </button>
        </div>
      </div>
    </div>
  );
}


function VisualizarComprovanteModal({ fileId, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [contentType, setContentType] = useState('');
  const [filename, setFilename] = useState('comprovante');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let revoke = null;
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${API}/comprovantes/${fileId}/download`, { responseType: 'blob' });
        if (cancelled) return;
        const ct = resp.headers['content-type'] || 'application/octet-stream';
        const url = URL.createObjectURL(resp.data);
        revoke = url;
        const cd = resp.headers['content-disposition'] || '';
        const m = cd.match(/filename="([^"]+)"/);
        if (m) setFilename(m[1]);
        setContentType(ct);
        setBlobUrl(url);
      } catch (e) { toast.error('Erro'); onClose(); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; if (revoke) URL.revokeObjectURL(revoke); };
  }, [fileId]);
  const isPdf = contentType.includes('pdf');
  const isImg = contentType.startsWith('image/');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="modal-visualizar-comprovante">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h3 className="font-bold text-sm truncate">{filename}</h3>
          <div className="flex items-center gap-2">
            {blobUrl && <a href={blobUrl} download={filename} className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded">Download</a>}
            <button onClick={onClose} className="hover:bg-gray-200 p-1.5 rounded"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-gray-100 flex items-center justify-center min-h-[400px]">
          {loading ? <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
            : isImg && blobUrl ? <img src={blobUrl} alt={filename} className="max-w-full max-h-[80vh] object-contain" />
            : isPdf && blobUrl ? <iframe src={blobUrl} title={filename} className="w-full h-[80vh] border-0" />
            : <a href={blobUrl} download={filename} className="px-4 py-2 bg-blue-600 text-white rounded">Baixar</a>}
        </div>
      </div>
    </div>
  );
}

